/**
 * Toast notification provider and hook.
 * Manages toast queue with max 3 visible at once.
 * Position: fixed top-right, z-index 130 (above modals at 120).
 */

import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Toast from './Toast';
import type { ToastVariant } from './Toast';

interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  showToast: (
    message: string,
    variant?: ToastVariant,
    options?: { duration?: number; action?: { label: string; onClick: () => void } }
  ) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE_TOASTS = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback(
    (
      message: string,
      variant: ToastVariant = 'info',
      options?: { duration?: number; action?: { label: string; onClick: () => void } }
    ) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      setToasts((prev) => {
        // If we already have max toasts, remove the oldest one
        const newToasts =
          prev.length >= MAX_VISIBLE_TOASTS ? prev.slice(1) : prev;

        return [
          ...newToasts,
          {
            id,
            message,
            variant,
            duration: options?.duration,
            action: options?.action,
          },
        ];
      });
    },
    []
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toastContainer =
    typeof document !== 'undefined' &&
    createPortal(
      <div
        role="region"
        aria-live="polite"
        aria-label="Notifications"
        className="fixed top-4 right-4 z-[130] flex flex-col gap-3 w-full max-w-sm pointer-events-none"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              id={toast.id}
              message={toast.message}
              variant={toast.variant}
              duration={toast.duration}
              action={toast.action}
              onDismiss={dismissToast}
            />
          </div>
        ))}
      </div>,
      document.body
    );

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      {toastContainer}
    </ToastContext.Provider>
  );
}

/**
 * Hook to access toast notification functions.
 * Must be used within a ToastProvider.
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
